import React, {useState, useEffect, useCallback} from 'react';
import {sortBy as _sortBy} from 'lodash';
import PropTypes from 'prop-types';
import {Button, Grid, Header, Segment} from 'semantic-ui-react';

import {hideModal, showModal} from '@utils/modal';
import EventContentSubtitleForm from '@modules/events/forms/EventContentSubtitleForm';
import {refetchQueryByName} from '@utils/apollo';
import {IconDelete, IconInfoCircleChangelog} from '@appComponents/IconCollection';
import {CircularProgress, Backdrop} from '@ui';
import Divider from '@ui/Divider';
import {convertToInt} from '@utils/helpers';
import {Authorization, hasPrivileges} from '@appComponents/Authorization';
import * as RESOURCES from '@constants/resources';
import * as CONST from '@constants/variables';
import {showMessageBox} from '@utils/messageBox';
import EventContentSubtitleModal from '@modules/events/components/EventContentSubtitleModal';
import {getStreamLanguageById} from '@modules/events/utils/eventContentHelper';

const columns = {
    tracks: {label: 'Track', width: 5},
    streamLanguage: {label: 'Language', width: 7},
    actions: {label: '', width: 4},
};

const EventContentSubtitleTable = (props) => {
    const {properties} = props;
    const [activeEditableFields, setActiveEditableFields] = useState(null);
    const [subtitles, setSubtitles] = useState([]);
    const [streamLanguages, setStreamLanguages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [encodingTargetId, setEncodingTargetId] = useState(properties?.encodingTargetId);
    const [authorization] = useState({
        update: hasPrivileges({[RESOURCES.SUBTITLE]: CONST.SECURITY_PRIVILEGES_UPDATE}),
    });

    const addMessageSuccessSave = useCallback(() => {
        showMessageBox(
            'eventSubtitleMessage',
            null,
            'The subtitle has been saved successfully.',
            'success'
        );
    }, []);

    const activateEditableCell = useCallback((event) => {
        setActiveEditableFields(convertToInt(event.currentTarget.dataset.id));
    }, []);

    const deactivateEditableCell = useCallback(() => {
        setActiveEditableFields(null);
    }, []);

    useEffect(() => {
        if (properties?.DataEventContentSubtitle) {
            const {subtitles, streamLanguages} = properties.DataEventContentSubtitle;
            setSubtitles(subtitles ? _sortBy(subtitles, 'creation_datetime') : []);
            setStreamLanguages(streamLanguages);
            setEncodingTargetId(properties.encodingTargetId);
        }
    }, [properties]);

    const handleSubtitleAdd = () => {
        setLoading(true);
        const dataToSave = Object.assign({}, {
            encodingTargetId: convertToInt(encodingTargetId),
            trackNumber: convertToInt(properties.subtitleToSave.trackNumber),
            streamLanguage: convertToInt(properties.subtitleToSave.streamLanguage) || '',
        });
        const {
            Form: {clearForm},
        } = properties;

        return properties.CreateMutation({variables: {...dataToSave}})
            .then(() => {
                addMessageSuccessSave();
                refetchQueryByName('getEventContentSubtitles');
                refetchQueryByName('GetEncodingTargetsForDistributions');
                refetchQueryByName('GetEventContentEncodingConfigurationChanged');
                clearForm('form_es_add');
                setLoading(false);
            })
            .catch(showErrorSubtitleNotSaved);
    };

    const reRenderModal = () => {
        showModal({
            isVisible: true,
            content: <EventContentSubtitleModal encodingTargetId={properties.encodingTargetId} key={2}/>,
            size: 'small',
        });
    };

    const handleSubtitleDelete = (id) => {
        properties.Modal.setModalConfirmation({
            header: <Header icon='trash' content='Delete subtitle'/>,
            text: 'Are you sure you want to remove this subtitle?',
            onYes: () => deleteSubtitle(id),
            onNo: () => reRenderModal(),
        });
    };

    const handleSubtitleUpdate = () => {
        setLoading(true);
        const subtitleToSave = properties.subtitleToUpdate;
        const dataToUpdate = Object.assign({}, {
            id: convertToInt(subtitleToSave.id),
            trackNumber: convertToInt(subtitleToSave.trackNumber),
            streamLanguage: convertToInt(subtitleToSave.streamLanguage) || '',
            encodingTargetId: convertToInt(properties.encodingTargetId),
        });

        return properties.UpdateMutation({variables: {...dataToUpdate}})
            .then(() => {
                addMessageSuccessSave();
                refetchQueryByName('getEventContentSubtitles');
                refetchQueryByName('GetEncodingTargetsForDistributions');
                refetchQueryByName('GetEventContentEncodingConfigurationChanged');
                setLoading(false);
            })
            .catch(showErrorSubtitleNotSaved);
    };

    const showErrorSubtitleNotSaved = (error) => {
        properties.MessageBox.addMessage(
            'eventSubtitleMessage',
            'The subtitle could not be saved.',
            `${error}`,
            'error'
        );
        setLoading(false);
    };

    const deleteSubtitle = (subtitleId) => {
        const dataToDelete = {...{id: subtitleId}};
        const {
            MessageBox: {addMessage},
        } = properties;
        properties.Modal.setModalConfirmation({text: 'The subtitle is being deleted...'});

        return properties.DeleteMutation({variables: {...dataToDelete}})
            .then(() => {
                addMessage('eventSubtitleMessage',
                    null,
                    'The subtitle has been deleted successfully.',
                    'success'
                );
                refetchQueryByName('GetEncodingTargetsForDistributions');
                refetchQueryByName('GetEventContentEncodingConfigurationChanged');
                reRenderModal();
            })
            .catch((error) => {
                addMessage(
                    'eventSubtitleMessage',
                    'The subtitle could not be deleted.',
                    `${error}`,
                    'error'
                );
            });
    };

    const renderButtonsForEditableRowInViewMode = (props, subtitleId) => (
        <div className='actions-list-icon'>
            <Authorization authorization={{
                [RESOURCES.SUBTITLE]: CONST.SECURITY_PRIVILEGES_DELETE,
            }}>
                <IconDelete icon='trash'
                    onClick={() => {handleSubtitleDelete(subtitleId.id);}}
                    onClickElement={subtitleId.id}
                />
            </Authorization>
            <IconInfoCircleChangelog
                tooltipProps={{hoverable: true, position: 'top right'}}
                resources={RESOURCES.SUBTITLE}
                data={subtitleId}
            />
        </div>
    );

    const renderEditableGrid = (data) => {
        const gridClass = authorization.update ? 'editableTable__trigger --audio_modal_row' : '--audio_modal_row',
            onClickCallback = authorization.update ? activateEditableCell : null;

        return (
            <Grid key={data.id} verticalAlign='middle'>
                <Grid.Row data-id={data.id} onClick={onClickCallback} className={gridClass}>
                    <Grid.Column width={columns.tracks.width}>
                        {data.track_number}
                    </Grid.Column>
                    <Grid.Column width={columns.streamLanguage.width}>
                        {getStreamLanguageById(data.stream_language?.id || '', streamLanguages)}
                    </Grid.Column>
                    <Grid.Column width={columns.actions.width}>
                        {renderButtonsForEditableRowInViewMode(props, data)}
                    </Grid.Column>
                </Grid.Row>
            </Grid>
        );
    };

    const renderEditForm = (data) => (
        <EventContentSubtitleForm
            form='form_es_update'
            loading={loading}
            formData={{
                streamLanguages,
                subtitles: {
                    id: data.id,
                    trackNumber: data.track_number,
                    streamLanguages: data.stream_language,
                },
            }}
            onUpdate={handleSubtitleUpdate}
            onDeactivateEditableCell={deactivateEditableCell}
        />
    );

    return (
        <div>
            <Backdrop open={loading} inverted>
                <CircularProgress />
            </Backdrop>
            <Header content='Subtitle configuration'/>
            <EventContentSubtitleTableHeader columns={columns}/>
            <EventContentSubtitleForm
                form='form_es_add'
                formData={{streamLanguages, subtitles}}
                onAdd={handleSubtitleAdd}
                loading={loading}
            />
            {subtitles.map((data, index) => ([
                <Divider key={`divider_${index}`}/>,
                (activeEditableFields === convertToInt(data.id))
                    ? renderEditForm(data)
                    : renderEditableGrid(data),
            ]))}
            <Segment className='--multiAudioModalFooter'>
                <Button onClick={hideModal} icon='close' content='Close'/>
            </Segment>
        </div>
    );
};

EventContentSubtitleTable.propTypes = {
    properties: PropTypes.object,
    subtitles: PropTypes.array,
    streamLanguages: PropTypes.array,
    encodingTargetId: PropTypes.number,
};

EventContentSubtitleTable.defaultProps = {
    subtitles: [],
    streamLanguages: [],
};

export default EventContentSubtitleTable;

const EventContentSubtitleTableHeader = ({columns}) => {
    const cells = Object.keys(columns).map((key) => (
        <Grid.Column key={key} width={columns[key].width}>
            {columns[key].label}
        </Grid.Column>
    ));

    return (
        <Grid className='editableTable__header --audio_header'>
            <Grid.Row>{cells}</Grid.Row>
        </Grid>
    );
};

EventContentSubtitleTableHeader.propTypes = {
    columns: PropTypes.object.isRequired,
};
